import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  User,
  Trophy,
  Award,
  Target,
  Music,
  Calendar,
  MapPin,
  Building2,
  TrendingUp,
  Camera,
  AtSign,
  Hash,
  PlayCircle,
  Globe,
  ExternalLink,
  AlertCircle,
  Star,
} from 'lucide-react';
import playerProfileService, { PlayerProfile, PlayerProfileMajorTitle } from '../services/playerProfileService';
import PublicProfilePage from './PublicProfilePage';

const TEAL = '#3FCED2';

function formatDateDe(ts?: number | null): string {
  if (!ts || typeof ts !== 'number') return '';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

function calcAge(dateOfBirth?: number | null, deathDate?: number | null): number | null {
  if (!dateOfBirth) return null;
  const end = deathDate ? new Date(deathDate) : new Date();
  const birth = new Date(dateOfBirth);
  let age = end.getFullYear() - birth.getFullYear();
  const m = end.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && end.getDate() < birth.getDate())) age--;
  return age;
}

function statusLabel(status?: string | null): string {
  switch (status) {
    case 'ACTIVE': return 'Aktiv';
    case 'RETIRED': return 'Zurückgetreten';
    case 'INACTIVE': return 'Inaktiv';
    case 'DECEASED': return 'Verstorben';
    default: return '';
  }
}

function statusColor(status?: string | null): string {
  switch (status) {
    case 'ACTIVE': return '#22C55E';
    case 'RETIRED': return '#94A3B8';
    case 'INACTIVE': return '#F59E0B';
    case 'DECEASED': return '#64748B';
    default: return '#94A3B8';
  }
}

interface StatItem {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

const PlayerProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [externalLink, setExternalLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    try {
      let p: PlayerProfile | null = await playerProfileService.getBySlug(slug);
      if (!p) {
        p = await playerProfileService.getById(slug);
      }
      if (!p) {
        setNotFound(true);
        setPlayer(null);
      } else {
        setPlayer(p);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Spielerprofils:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [load]);

  const displayName = useMemo(() => playerProfileService.displayName(player), [player]);
  const nickname = useMemo(() => playerProfileService.displayNickname(player), [player]);

  const heroUrl = useMemo(() => playerProfileService.buildFileUrl(player?.heroImage), [player]);
  const profileUrl = useMemo(() => playerProfileService.buildFileUrl(player?.profileImage), [player]);

  const stats: StatItem[] = useMemo(() => {
    if (!player?.careerStats) return [];
    const cs = player.careerStats;
    const items: StatItem[] = [];
    if (cs.averageAllTime != null) items.push({ label: 'Karriere-Average', value: cs.averageAllTime.toFixed(2), icon: <TrendingUp className="w-5 h-5" /> });
    if (cs.highestAverage != null) items.push({ label: 'Höchster Average', value: cs.highestAverage.toFixed(2), icon: <Star className="w-5 h-5" /> });
    if (cs.highestCheckout != null) items.push({ label: 'Höchstes Finish', value: cs.highestCheckout, icon: <Target className="w-5 h-5" /> });
    if (cs.nineDarters != null) items.push({ label: '9-Darter', value: cs.nineDarters, icon: <Award className="w-5 h-5" /> });
    if (cs.majorTitles != null) items.push({ label: 'Major Titel', value: cs.majorTitles, icon: <Trophy className="w-5 h-5" /> });
    if (cs.worldChampionshipTitles != null) items.push({ label: 'WM Titel', value: cs.worldChampionshipTitles, icon: <Trophy className="w-5 h-5" /> });
    if (cs.worldCupTitles != null) items.push({ label: 'World Cup', value: cs.worldCupTitles, icon: <Trophy className="w-5 h-5" /> });
    if (cs.matchplayTitles != null) items.push({ label: 'Matchplay', value: cs.matchplayTitles, icon: <Trophy className="w-5 h-5" /> });
    if (cs.premierLeagueTitles != null) items.push({ label: 'Premier League', value: cs.premierLeagueTitles, icon: <Trophy className="w-5 h-5" /> });
    return items;
  }, [player]);

  const socialLinks = useMemo(() => {
    const sm = player?.socialMedia;
    if (!sm) return [] as { label: string; url: string; icon: React.ReactNode; color: string }[];
    const links: { label: string; url: string; icon: React.ReactNode; color: string }[] = [];
    if (sm.instagram) links.push({ label: 'Instagram', url: sm.instagram.startsWith('http') ? sm.instagram : `https://instagram.com/${sm.instagram.replace('@', '')}`, icon: <Camera className="w-4 h-4" />, color: '#E1306C' });
    if (sm.twitter) links.push({ label: 'X / Twitter', url: sm.twitter.startsWith('http') ? sm.twitter : `https://twitter.com/${sm.twitter.replace('@', '')}`, icon: <AtSign className="w-4 h-4" />, color: '#1DA1F2' });
    if (sm.facebook) links.push({ label: 'Facebook', url: sm.facebook.startsWith('http') ? sm.facebook : `https://facebook.com/${sm.facebook}`, icon: <Hash className="w-4 h-4" />, color: '#1877F2' });
    if (sm.tiktok) links.push({ label: 'TikTok', url: sm.tiktok.startsWith('http') ? sm.tiktok : `https://tiktok.com/@${sm.tiktok.replace('@', '')}`, icon: <Music className="w-4 h-4" />, color: '#000000' });
    if (sm.youtube) links.push({ label: 'YouTube', url: sm.youtube.startsWith('http') ? sm.youtube : `https://youtube.com/@${sm.youtube.replace('@', '')}`, icon: <PlayCircle className="w-4 h-4" />, color: '#FF0000' });
    if (sm.website) links.push({ label: 'Website', url: sm.website.startsWith('http') ? sm.website : `https://${sm.website}`, icon: <Globe className="w-4 h-4" />, color: TEAL });
    return links;
  }, [player]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-zinc-400 text-sm">Lade Spielerprofil...</div>
      </div>
    );
  }

  if (notFound || !player) {
    // Kein Pro-Spieler unter diesem Slug gefunden — Community-Spielerprofil
    // versuchen (Flutter nutzt /spieler/:slug für beide Profil-Arten).
    // PublicProfilePage bringt einen eigenen "nicht gefunden"-Zustand mit.
    return <PublicProfilePage />;
  }

  const age = calcAge(player.dateOfBirth, player.deathDate);
  const flag = playerProfileService.flag(player);

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{player.seoTitle || `${displayName}${nickname ? ` "${nickname}"` : ''} - DartCloud`}</title>
        {player.seoDescription && <meta name="description" content={player.seoDescription} />}
      </Helmet>

      {/* Hero Header */}
      <div className="relative w-full" style={{ height: '320px' }}>
        {heroUrl ? (
          <>
            <img src={heroUrl} alt={displayName} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/90" />
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${TEAL} 0%, #2A9D8F 100%)`,
            }}
          />
        )}

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Profile Info (overlapping hero) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-20 sm:-mt-24 flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6">
          {/* Avatar */}
          <div
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 flex-shrink-0 shadow-2xl"
            style={{ borderColor: '#0a0a0a' }}
          >
            {profileUrl ? (
              <img src={profileUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: TEAL }}
              >
                <span className="text-5xl sm:text-6xl font-bold text-white">
                  {displayName ? displayName[0].toUpperCase() : '?'}
                </span>
              </div>
            )}
          </div>

          {/* Name + meta */}
          <div className="flex-1 text-center sm:text-left pb-2 sm:pb-4">
            <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">{displayName || '—'}</h1>
              <span className="text-2xl sm:text-3xl" aria-label={player.nationality || ''}>{flag}</span>
              {player.verified && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${TEAL}33`, color: TEAL }}
                >
                  Verifiziert
                </span>
              )}
            </div>
            {nickname && (
              <div className="mt-1 text-base sm:text-lg text-zinc-300 italic">"{nickname}"</div>
            )}
            <div className="mt-2 flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 text-sm text-zinc-400">
              {player.status && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: statusColor(player.status) }}
                  />
                  {statusLabel(player.status)}
                </span>
              )}
              {player.organization && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {player.organization}
                </span>
              )}
              {player.worldRanking != null && (
                <span className="inline-flex items-center gap-1">
                  <Trophy className="w-4 h-4" />
                  WR #{player.worldRanking}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8 pb-12">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            {player.bio && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5" style={{ color: TEAL }} />
                  <h2 className="text-lg font-semibold">Über</h2>
                </div>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{player.bio}</p>
              </div>
            )}

            {/* Career Stats */}
            {stats.length > 0 && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5" style={{ color: TEAL }} />
                  <h2 className="text-lg font-semibold">Karriere-Statistiken</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {stats.map((s, i) => (
                    <div
                      key={i}
                      className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-2 text-zinc-400 mb-2" style={{ color: TEAL }}>
                        {s.icon}
                      </div>
                      <div className="text-xl font-bold">{s.value}</div>
                      <div className="text-xs text-zinc-400 mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Major Titles */}
            {Array.isArray(player.majorTitles) && player.majorTitles.length > 0 && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5" style={{ color: TEAL }} />
                  <h2 className="text-lg font-semibold">Major-Titel</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-400 border-b border-zinc-800">
                        <th className="text-left py-2 px-2 font-medium">Jahr</th>
                        <th className="text-left py-2 px-2 font-medium">Turnier</th>
                        <th className="text-left py-2 px-2 font-medium hidden sm:table-cell">Gegner</th>
                        <th className="text-left py-2 px-2 font-medium hidden md:table-cell">Ergebnis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {player.majorTitles.map((t: PlayerProfileMajorTitle, i) => (
                        <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                          <td className="py-2 px-2 font-semibold" style={{ color: TEAL }}>
                            {t.year ?? '—'}
                          </td>
                          <td className="py-2 px-2">{t.tournament ?? '—'}</td>
                          <td className="py-2 px-2 text-zinc-400 hidden sm:table-cell">{t.opponent ?? '—'}</td>
                          <td className="py-2 px-2 text-zinc-400 hidden md:table-cell">{t.score ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Images Gallery */}
            {Array.isArray(player.images) && player.images.length > 0 && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-4">Galerie</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {player.images.map((img, i) => {
                    const url = playerProfileService.buildFileUrl(img);
                    if (!url) return null;
                    return (
                      <div
                        key={i}
                        className="aspect-square rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950"
                      >
                        <img src={url} alt={`${displayName} ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Personal Info */}
            <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-base font-semibold mb-4">Persönliche Daten</h3>
              <div className="space-y-3 text-sm">
                {player.dateOfBirth && (
                  <InfoRow icon={<Calendar className="w-4 h-4" />} label="Geboren">
                    {formatDateDe(player.dateOfBirth)}
                    {age != null && <span className="text-zinc-500"> ({age} Jahre)</span>}
                  </InfoRow>
                )}
                {player.deathDate && (
                  <InfoRow icon={<Calendar className="w-4 h-4" />} label="Verstorben">
                    {formatDateDe(player.deathDate)}
                  </InfoRow>
                )}
                {player.placeOfBirth && (
                  <InfoRow icon={<MapPin className="w-4 h-4" />} label="Geburtsort">
                    {player.placeOfBirth}
                  </InfoRow>
                )}
                {player.nationality && (
                  <InfoRow icon={<span className="text-base">{flag}</span>} label="Nationalität">
                    {player.nationality}
                  </InfoRow>
                )}
                {player.turnedPro != null && (
                  <InfoRow icon={<Trophy className="w-4 h-4" />} label="Profi seit">
                    {player.turnedPro}
                  </InfoRow>
                )}
                {player.careerHigh != null && (
                  <InfoRow icon={<TrendingUp className="w-4 h-4" />} label="Career High">
                    #{player.careerHigh}
                  </InfoRow>
                )}
                {player.walkOnSong && (
                  <InfoRow icon={<Music className="w-4 h-4" />} label="Walk-On">
                    {player.walkOnSong}
                  </InfoRow>
                )}
              </div>
            </div>

            {/* Equipment */}
            {(player.dartWeight != null || player.dartBrand) && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5" style={{ color: TEAL }} />
                  <h3 className="text-base font-semibold">Equipment</h3>
                </div>
                <div className="space-y-3 text-sm">
                  {player.dartBrand && (
                    <InfoRow icon={<Target className="w-4 h-4" />} label="Marke">
                      {player.dartBrand}
                    </InfoRow>
                  )}
                  {player.dartWeight != null && (
                    <InfoRow icon={<Target className="w-4 h-4" />} label="Gewicht">
                      {player.dartWeight} g
                    </InfoRow>
                  )}
                </div>
              </div>
            )}

            {/* Social Media */}
            {socialLinks.length > 0 && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5" style={{ color: TEAL }} />
                  <h3 className="text-base font-semibold">Social Media</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map((l, i) => (
                    <button
                      key={i}
                      onClick={() => setExternalLink(l.url)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm transition-colors"
                      style={{
                        borderColor: `${l.color}55`,
                        backgroundColor: `${l.color}15`,
                        color: l.color === '#000000' ? '#fff' : l.color,
                      }}
                    >
                      {l.icon}
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {Array.isArray(player.tags) && player.tags.length > 0 && (
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-6">
                <h3 className="text-base font-semibold mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {player.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-full text-xs bg-zinc-800 text-zinc-300"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* External link modal */}
      {externalLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setExternalLink(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">Externer Link</h3>
                <p className="text-sm text-zinc-400">
                  Du verlässt DartCloud. Wir übernehmen keine Verantwortung für den Inhalt externer Webseiten.
                </p>
              </div>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-300 break-all mb-4">
              {externalLink}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setExternalLink(null)}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm"
              >
                Abbrechen
              </button>
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setExternalLink(null)}
                className="px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2"
                style={{ backgroundColor: TEAL, color: '#000' }}
              >
                <ExternalLink className="w-4 h-4" />
                Öffnen
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="text-zinc-500 mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-zinc-200">{children}</div>
    </div>
  </div>
);

export default PlayerProfilePage;
